import React, { useEffect, useState, useMemo } from "react";
import { usePersistedState } from "@/hooks/usePersistedState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Calendar,
  Clock,
  User,
  Phone,
  MapPin,
  Search,
  Filter,
  Trash2,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import {
  format,
  parseISO,
  isToday,
  isTomorrow,
  isYesterday,
  addDays,
  startOfDay,
  isSameDay,
} from "date-fns";
import { parseApiTimestamp } from "@/lib/datetime";
import { api } from "@/lib/api";
import { UpcomingCall } from "@/lib/types";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { Skeleton } from "@/components/ui/skeleton";

type SortField = "patient_name" | "phone_number" | "schedule_datetime" | null;
type SortDirection = "asc" | "desc";

const UpcomingCallsPage: React.FC = () => {
  const [upcomingCalls, setUpcomingCalls] = useState<UpcomingCall[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = usePersistedState("view:upcoming-calls:search", "");
  const [statusFilter, setStatusFilter] = usePersistedState("view:upcoming-calls:status", "all");
  const [dateFilter, setDateFilter] = usePersistedState("view:upcoming-calls:date", "all");
  const [selectedCallIds, setSelectedCallIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortField, setSortField] = usePersistedState<SortField>("view:upcoming-calls:sortField", null);
  const [sortDirection, setSortDirection] = usePersistedState<SortDirection>("view:upcoming-calls:sortDirection", "asc");
  const { selectedOrganizationId } = useOrganizationsStore();

  useEffect(() => {
    const fetchUpcomingCalls = async () => {
      if (!selectedOrganizationId) return;

      setIsLoading(true);
      setError(null);
      try {
        const calls = await api.getUpcomingCalls(selectedOrganizationId);
        setUpcomingCalls(calls);
      } catch (err) {
        setError("Failed to fetch upcoming calls");
        console.error("Error fetching upcoming calls:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUpcomingCalls();
  }, [selectedOrganizationId]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return null;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  // Filter and search logic
  const filteredCalls = useMemo(() => {
    let filtered = upcomingCalls.filter((call) => {
      // Search filter
      const patientName = call.patient
        ? `${call.patient.preferred_name || call.patient.first_name} ${
            call.patient.last_name
          }`.toLowerCase()
        : "";
      const matchesSearch =
        searchTerm === "" || patientName.includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus =
        statusFilter === "all" ||
        call.scheduled_call.status.toLowerCase() === statusFilter;

      // Date filter
      let matchesDate = true;
      if (dateFilter !== "all") {
        const callDate = parseApiTimestamp(call.scheduled_call.schedule_timestamp) ?? new Date(Number.NaN);
        if (dateFilter === "today") {
          matchesDate = isToday(callDate);
        } else if (dateFilter === "tomorrow") {
          matchesDate = isTomorrow(callDate);
        }
      }

      return matchesSearch && matchesStatus && matchesDate;
    });

    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue: string | number = "";
        let bValue: string | number = "";

        switch (sortField) {
          case "patient_name":
            aValue = a.patient
              ? `${a.patient.preferred_name || a.patient.first_name} ${
                  a.patient.last_name
                }`.toLowerCase()
              : "";
            bValue = b.patient
              ? `${b.patient.preferred_name || b.patient.first_name} ${
                  b.patient.last_name
                }`.toLowerCase()
              : "";
            break;
          case "phone_number":
            aValue = a.patient?.phone_number || "";
            bValue = b.patient?.phone_number || "";
            break;
          case "schedule_datetime":
            aValue = new Date(a.scheduled_call.schedule_local_time).getTime();
            bValue = new Date(b.scheduled_call.schedule_local_time).getTime();
            break;
        }

        if (sortField === "schedule_datetime") {
          // For datetime, compare as numbers
          return sortDirection === "asc"
            ? (aValue as number) - (bValue as number)
            : (bValue as number) - (aValue as number);
        } else {
          // For strings, use localeCompare
          const result = (aValue as string).localeCompare(bValue as string);
          return sortDirection === "asc" ? result : -result;
        }
      });
    }

    return filtered;
  }, [
    upcomingCalls,
    searchTerm,
    statusFilter,
    dateFilter,
    sortField,
    sortDirection,
  ]);

  // Calculate calls per day for the next 7 days
  const dailyBreakdown = useMemo(() => {
    const days = [];
    const today = startOfDay(new Date());

    for (let i = 0; i < 7; i++) {
      const date = addDays(today, i);
      const callsForDay = upcomingCalls.filter((call) => {
        const callDate = startOfDay(
          parseISO(call.scheduled_call.schedule_local_time)
        );
        return isSameDay(callDate, date);
      });

      let dayLabel;
      if (i === 0) {
        dayLabel = "Today";
      } else if (i === 1) {
        dayLabel = "Tomorrow";
      } else {
        dayLabel = format(date, "EEE"); // Mon, Tue, etc.
      }

      days.push({
        date,
        label: dayLabel,
        count: callsForDay.length,
        fullDate: format(date, "MMM d"),
      });
    }

    return days;
  }, [upcomingCalls]);

  const handleSelectAll = () => {
    if (selectedCallIds.length === filteredCalls.length) {
      setSelectedCallIds([]);
    } else {
      setSelectedCallIds(filteredCalls.map((call) => call.call_id));
    }
  };

  const handleSelectCall = (callId: string) => {
    setSelectedCallIds((prev) =>
      prev.includes(callId)
        ? prev.filter((id) => id !== callId)
        : [...prev, callId]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedCallIds.length === 0) return;

    setIsDeleting(true);
    try {
      await api.deleteMultipleSchedules(selectedCallIds);
      // Refresh the calls list
      const calls = await api.getUpcomingCalls(selectedOrganizationId!);
      setUpcomingCalls(calls);
      setSelectedCallIds([]);
    } catch (err) {
      console.error("Error deleting scheduled calls:", err);
      setError("Failed to delete selected calls");
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "scheduled") {
      return <Badge variant="default">Scheduled</Badge>;
    } else if (statusLower === "pending") {
      return <Badge variant="outline">Pending</Badge>;
    } else if (statusLower === "cancelled") {
      return <Badge variant="destructive">Cancelled</Badge>;
    } else {
      return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDateTime = (dateString: string, localTime: string) => {
    // schedule_local_time is the wall-clock time in the call's own time zone
    // (naive on purpose), so it is shown as-is, not converted.
    const dateTime = parseISO(localTime);

    if (isToday(dateTime)) {
      return `Today ${format(dateTime, "h:mm a")}`;
    } else if (isTomorrow(dateTime)) {
      return `Tomorrow ${format(dateTime, "h:mm a")}`;
    } else if (isYesterday(dateTime)) {
      return `Yesterday ${format(dateTime, "h:mm a")}`;
    } else {
      // For dates more than 1 day away, show the date
      const now = new Date();
      const diffInDays =
        Math.abs(dateTime.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

      if (diffInDays < 7) {
        // Within a week, show day of week
        return format(dateTime, "EEEE h:mm a");
      } else {
        // More than a week, show date
        return format(dateTime, "MMM d, yyyy h:mm a");
      }
    }
  };

  if (error) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight">Upcoming Calls</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-destructive">
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl min-w-0 space-y-4 p-4 md:space-y-6 md:p-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Upcoming Calls</h1>
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          {selectedCallIds.length > 0 && (
            <Button
              onClick={handleDeleteSelected}
              disabled={isDeleting}
              variant="destructive"
              size="sm"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {isDeleting
                ? "Deleting..."
                : `Delete ${selectedCallIds.length} call${
                    selectedCallIds.length !== 1 ? "s" : ""
                  }`}
            </Button>
          )}
          <div className="text-sm text-muted-foreground">
            {filteredCalls.length} of {upcomingCalls.length} call
            {upcomingCalls.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Daily Breakdown */}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
        {dailyBreakdown.map((day, index) => (
          <div
            key={day.date.toISOString()}
            className="flex shrink-0 flex-col items-center justify-center bg-card rounded-lg border p-3 min-w-[70px]"
          >
            <div className="text-xs font-medium text-muted-foreground mb-1">
              {day.label}
            </div>
            <div className="text-xs text-muted-foreground mb-1">{day.fullDate}</div>
            <div className="text-lg font-bold text-foreground">{day.count}</div>
          </div>
        ))}
      </div>

      {/* Search and Filter Controls */}
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 text-muted-foreground/60 transform -translate-y-1/2" />
                <Input
                  placeholder="Search by patient name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 md:flex">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dates</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="tomorrow">Tomorrow</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex space-x-4">
                    <Skeleton className="h-4 w-1/4" />
                    <Skeleton className="h-4 w-1/6" />
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-4 w-1/6" />
                  </div>
                ))}
              </div>
            </div>
          ) : filteredCalls.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground/60 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {upcomingCalls.length === 0
                  ? "No upcoming calls"
                  : "No calls match your filters"}
              </h3>
              <p className="text-muted-foreground">
                {upcomingCalls.length === 0
                  ? "All scheduled calls will appear here."
                  : "Try adjusting your search or filter criteria."}
              </p>
            </div>
          ) : (
            <>
            {/* Phone: card list */}
            <div className="md:hidden">
              <div className="flex items-center gap-3 border-b px-4 py-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={
                    selectedCallIds.length === filteredCalls.length &&
                    filteredCalls.length > 0
                  }
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all calls"
                />
                <span>Select all</span>
              </div>
              <div className="divide-y">
                {filteredCalls.map((call) => {
                  const patientName = call.patient
                    ? `${call.patient.preferred_name || call.patient.first_name} ${call.patient.last_name}`
                    : null;
                  return (
                    <div key={call.call_id} className="flex items-start gap-3 p-4">
                      <div className="pt-0.5">
                        <Checkbox
                          checked={selectedCallIds.includes(call.call_id)}
                          onCheckedChange={() => handleSelectCall(call.call_id)}
                          aria-label={`Select call for ${patientName || "unknown patient"}`}
                        />
                      </div>
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 truncate font-medium">
                            {patientName || (
                              <span className="text-muted-foreground/60">No patient assigned</span>
                            )}
                          </div>
                          <div className="shrink-0">{getStatusBadge(call.scheduled_call.status)}</div>
                        </div>
                        {call.patient?.phone_number && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{call.patient.phone_number}</span>
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                          <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                          <span>
                            {formatDateTime(
                              call.scheduled_call.schedule_timestamp,
                              call.scheduled_call.schedule_local_time
                            )}
                          </span>
                          {call.scheduled_call.flexible_time_window && (
                            <Badge variant="outline" className="text-xs">
                              Flexible
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{call.scheduled_call.timezone}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tablet / desktop: table */}
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedCallIds.length === filteredCalls.length &&
                        filteredCalls.length > 0
                      }
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all calls"
                    />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort("patient_name")}
                  >
                    Patient Name{getSortIcon("patient_name")}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort("phone_number")}
                  >
                    Phone Number{getSortIcon("phone_number")}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort("schedule_datetime")}
                  >
                    Scheduled Date/Time{getSortIcon("schedule_datetime")}
                  </TableHead>
                  <TableHead>Timezone</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCalls.map((call) => (
                  <TableRow key={call.call_id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedCallIds.includes(call.call_id)}
                        onCheckedChange={() => handleSelectCall(call.call_id)}
                        aria-label={`Select call for ${
                          call.patient
                            ? `${
                                call.patient.preferred_name ||
                                call.patient.first_name
                              } ${call.patient.last_name}`
                            : "unknown patient"
                        }`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {call.patient ? (
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground/60" />
                          {call.patient.preferred_name ||
                            call.patient.first_name}{" "}
                          {call.patient.last_name}
                        </div>
                      ) : (
                        <span className="text-muted-foreground/60">
                          No patient assigned
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {call.patient?.phone_number ? (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground/60" />
                          {call.patient.phone_number}
                        </div>
                      ) : (
                        <span className="text-muted-foreground/60">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground/60" />
                        <span
                          title={format(
                            parseISO(call.scheduled_call.schedule_local_time),
                            "EEEE, MMMM d, yyyy 'at' h:mm:ss a"
                          )}
                          className="cursor-help"
                        >
                          {formatDateTime(
                            call.scheduled_call.schedule_timestamp,
                            call.scheduled_call.schedule_local_time
                          )}
                        </span>
                        {call.scheduled_call.flexible_time_window && (
                          <Badge variant="outline" className="text-xs">
                            Flexible
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground/60" />
                        {call.scheduled_call.timezone}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(call.scheduled_call.status)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UpcomingCallsPage;
